// ─── Email Validation ───
// Only allows: letters (a-z), numbers (0-9), periods (.)
// No special characters, no period before @, must have valid domain
export const isValidEmail = (email) => {
  const trimmed = email.trim();

  // Check for disallowed characters (only a-z, 0-9, . and @ allowed)
  if (/[^a-zA-Z0-9.@]/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, only letters (a-z), numbers (0-9), and periods (.) are allowed",
    };
  }

  // Check for period immediately before @
  if (/\.@/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, the last character before @ must be a letter (a-z) or number (0-9)",
    };
  }

  // Check full email format: user@domain.tld
  if (!/^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
    return {
      valid: false,
      message: "Please enter a valid email address",
    };
  }

  return { valid: true, message: "" };
};

// ─── Mobile Number Validation (exactly 10 digits, numbers only) ───
export const isValidMobile = (mobile) => {
  const mobileRegex = /^[0-9]{10}$/;
  return mobileRegex.test(mobile);
};

// ─── Pincode Validation (exactly 6 digits) ───
export const isValidPincode = (pincode) => {
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode);
};

// ─── Website URL Validation ───
export const isValidWebsite = (url) => {
  return /^https?:\/\/.+/i.test(url.trim());
};

// ─── Helper: Validate a single email field ───
const validateEmailField = (errors, fieldName, value, label) => {
  if (!value?.trim()) {
    errors[fieldName] = `${label} is required`;
  } else {
    const emailResult = isValidEmail(value);
    if (!emailResult.valid) {
      errors[fieldName] = emailResult.message;
    }
  }
};

// ─── Helper: Validate a single mobile field ───
const validateMobileField = (
  errors,
  fieldName,
  value,
  label,
  required = true,
) => {
  if (!value?.trim()) {
    if (required) {
      errors[fieldName] = `${label} is required`;
    }
  } else if (/\D/.test(value)) {
    errors[fieldName] =
      "Please enter numbers only. Letters and special characters are not allowed";
  } else if (!isValidMobile(value)) {
    errors[fieldName] = `${label} must be exactly 10 digits`;
  }
};

// ─── School Setup Form Validation ───
export const validateSchoolSetupForm = (formData, sameAsPrincipal) => {
  const errors = {};

  // ── School Name ──
  if (!formData.schoolName?.trim()) {
    errors.schoolName = "School Name is required";
  }

  // ── School Short Name ──
  if (!formData.schoolShortName?.trim()) {
    errors.schoolShortName = "School Short Name is required";
  }

  // ── School Email Address ──
  validateEmailField(
    errors,
    "emailAddress",
    formData.emailAddress,
    "Email Address",
  );

  // ── Website Address ──
  if (!formData.websiteAddress?.trim()) {
    errors.websiteAddress = "Website Address is required";
  } else if (!isValidWebsite(formData.websiteAddress)) {
    errors.websiteAddress =
      "Website must start with https:// (e.g., https://example.com)";
  }

  // ── Address 1 ──
  if (!formData.address1?.trim()) {
    errors.address1 = "Address 1 is required";
  }

  // ── City ──
  if (!formData.city?.trim()) {
    errors.city = "City is required";
  }

  // ── State ──
  if (!formData.state) {
    errors.state = "State is required";
  }

  // ── Country ──
  if (!formData.country) {
    errors.country = "Country is required";
  }

  // ── Pincode (exactly 6 digits) ──
  if (!formData.pincode?.trim()) {
    errors.pincode = "Pincode is required";
  } else if (/\D/.test(formData.pincode)) {
    errors.pincode = "Pincode must contain numbers only";
  } else if (!isValidPincode(formData.pincode)) {
    errors.pincode = "Pincode must be exactly 6 digits";
  }

  // ── Mobile Number ──
  validateMobileField(
    errors,
    "mobileNumber",
    formData.mobileNumber,
    "Mobile Number",
  );

  // ── School Mobile Number (optional but validated if provided) ──
  validateMobileField(
    errors,
    "schoolMobileNumber",
    formData.schoolMobileNumber,
    "School Mobile Number",
    false,
  );

  // ── School Category ──
  if (!formData.schoolCategory) {
    errors.schoolCategory = "School Category is required";
  }

  // ── Board Affiliation ──
  if (!formData.boardAffiliation) {
    errors.boardAffiliation = "Board Affiliation is required";
  }

  // ── Approving Authority ──
  if (!formData.approvingAuthority) {
    errors.approvingAuthority = "Approving Authority is required";
  }

  // ── Medium of Study ──
  if (!formData.mediumOfStudy) {
    errors.mediumOfStudy = "Medium of Study is required";
  }

  // ── Principal First Name ──
  if (!formData.principalFirstName?.trim()) {
    errors.principalFirstName = "First Name is required";
  }

  // ── Principal Last Name ──
  if (!formData.principalLastName?.trim()) {
    errors.principalLastName = "Last Name is required";
  }

  // ── Principal Email ──
  validateEmailField(
    errors,
    "principalEmail",
    formData.principalEmail,
    "Email Address",
  );

  // ── Principal Mobile ──
  validateMobileField(
    errors,
    "principalMobile",
    formData.principalMobile,
    "Mobile Number",
  );

  // ── Admin Validations (only if NOT same as principal) ──
  if (!sameAsPrincipal) {
    if (!formData.adminFirstName?.trim()) {
      errors.adminFirstName = "First Name is required";
    }
    if (!formData.adminLastName?.trim()) {
      errors.adminLastName = "Last Name is required";
    }

    // Admin Email
    validateEmailField(
      errors,
      "adminEmail",
      formData.adminEmail,
      "Email Address",
    );

    // Admin Mobile
    validateMobileField(
      errors,
      "adminMobile",
      formData.adminMobile,
      "Mobile Number",
    );
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};
